import React, { useRef, useState, useEffect, useCallback } from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import DraggableObject from "./DraggableObject";
import { CircleSymbolType, zIndexEpsilon } from "../../types/floorplannerTypes";
import { SelectableSymbol, BoundingBox } from "./SelectableSymbol";
import { projectToWorld } from "./projectToWorld";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import { Line } from "@react-three/drei";
import { editorStore } from "../../store/editorStore";
import { observer } from "mobx-react-lite";
import PatternFillMesh from "./PatternFillMesh";
import ShapeProjectionOverlay from "./ShapeProjectionOverlay";
import { shouldShowShapePrimitiveProjection } from "../../features/floorplan3d/symbols/primitives/shapes/shapePrimitiveGeometry";
import { canvasAccentColor } from "../../constants/brandColors";
import { resolveFillOpacity } from "../../utils/fillOpacity";
import { getOpacityRemountEpoch } from "../../utils/opacityRemount";
import { isSymbolAspectRatioLocked } from "../../utils/symbolAspectRatio";
import {
  isSymbolAbovePlanCutPlane,
  PLAN_OVERHEAD_DASH,
} from "../../utils/planCutPlane";

interface CircleSymbolProps {
  circle: CircleSymbolType;
  onDragStart: (circle: CircleSymbolType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
}

const CircleSymbol: React.FC<CircleSymbolProps> = observer(({
  circle,
  onDragStart,
  onDragEnd,
  onDrag,
}) => {
  const { gl, camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const floorplannerStore = React.useContext(FloorplannerStoreContext);

  const lineWeight = circle.lineWeight || floorplannerStore.symbolLineWeight;
  const [circleWidth, setCircleWidth] = useState(circle.circleWidth);
  const [circleHeight, setCircleHeight] = useState(circle.circleHeight);
  const [localPosition, setLocalPosition] = useState<[number, number]>([circle.position.x, circle.position.y]);

  const flipY = circle.flipVertical ? -1 : 1; // Flip vertically
  // Center and pivot are at origin since bounding box is centered
  const centerPosition: [number, number] = [0, 0];
  const lineColor = circle.lineColor || floorplannerStore.lineColor;
  const baseRenderOrder = circle.zIndex ?? 0;
  const fillOpacity = resolveFillOpacity(circle);
  const fillIsTransparent = fillOpacity < 1;
  // Above-cut objects keep full-opacity strokes (Style is authoritative); ghost
  // projection is only for tilt/wall below the cut.
  const aboveCutPlane = isSymbolAbovePlanCutPlane(circle);
  const projectionActive =
    shouldShowShapePrimitiveProjection(circle) && !aboveCutPlane;
  const useDashed =
    projectionActive || circle.lineType === "dashed";
  const pivot: [number, number] = [0, 0];
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const noopRaycast = () => null;

  // Calculate the bounding box of the circle - returns corners relative to center (0,0)
  const calculateBoundingBox = useCallback((): BoundingBox => {
    if (!groupRef.current) {
      return {
        topLeft: [0, 0],
        topRight: [0, 0],
        bottomLeft: [0, 0],
        bottomRight: [0, 0],
        width: 0,
        height: 0,
        depth: 0,
      };
    }

    // Clone the object
    const tempGroup = groupRef.current.clone();

    // Save the original rotation
    const originalRotation = tempGroup.rotation.clone();

    // Temporarily reset the rotation to 0
    tempGroup.rotation.set(0, 0, 0);

    // Calculate the bounding box as if the object had no rotation
    const boundingBox = new THREE.Box3().setFromObject(tempGroup);
    const size = new THREE.Vector3();
    boundingBox.getSize(size);

    // Restore the original rotation
    tempGroup.rotation.copy(originalRotation);

    // Add line weight adjustment
    const lineWeightAdjustment = lineWeight / 100;
    const width = size.x + lineWeightAdjustment;
    const height = size.y + lineWeightAdjustment;
    const halfWidth = width / 2;
    const halfHeight = height / 2;

    // Return corners relative to center (0, 0) - consistent with how SvgSymbol works
    return {
      topLeft: [-halfWidth, halfHeight],
      topRight: [halfWidth, halfHeight],
      bottomLeft: [-halfWidth, -halfHeight],
      bottomRight: [halfWidth, -halfHeight],
      width: width,
      height: height,
      depth: 0,
    };
  }, [circle.position.x, circle.position.y, circle.flipHorizontal, circle.flipVertical, circleWidth, circleHeight, lineWeight]);

  const dragOffset = useRef({ x: 0, y: 0 });

  const onHandleDrag = (event: PointerEvent) => {
    const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const dy = worldY - circle.position.y - dragOffset.current.y;
    // Position is handled by the store, no longer need to update centerPosition
  };

  const onPointerUpHandle = () => {
    gl.domElement.removeEventListener("pointermove", onHandleDrag);
    gl.domElement.removeEventListener("pointerup", onPointerUpHandle);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.setDirty();
  };

  const handleDrag = (newPosition: [number, number]) => {
    onDrag(newPosition);
  };
  // Call the function and destructure the returned values
  //const { rectPoints, extraLines } = generateRectPointsAndLines();

  // Handle Drag Right (Green Line)
  const dragOffsetRight = useRef({ x: 0, y: 0 });

  const onHandleDragRight = (event: PointerEvent) => {
    const [worldX] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const dx = worldX - circle.position.x - dragOffsetRight.current.x;
    floorplannerStore.updateSymbolProperty(circle.id, "circleWidth", Math.max(0.1, circleWidth + dx));
  };

  const onPointerUpHandleRight = () => {
    gl.domElement.removeEventListener("pointermove", onHandleDragRight);
    gl.domElement.removeEventListener("pointerup", onPointerUpHandleRight);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.updateSymbolProperty(circle.id, "circleWidth", circleWidth);
    floorplannerStore.setDirty();
  };

  // Handle Drag Top (Red Line)
  const dragOffsetTop = useRef({ x: 0, y: 0 });

  const onHandleDragTop = (event: PointerEvent) => {
    const [, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const dy = worldY - circle.position.y - dragOffsetTop.current.y;
    floorplannerStore.updateSymbolProperty(circle.id, "circleHeight", Math.max(0.1, circleHeight + dy * flipY));
  };

  const onPointerUpHandleTop = () => {
    gl.domElement.removeEventListener("pointermove", onHandleDragTop);
    gl.domElement.removeEventListener("pointerup", onPointerUpHandleTop);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.updateSymbolProperty(circle.id, "circleHeight", circleHeight);
    floorplannerStore.setDirty();
  };
  // Function to generate 3D points for the circle (adding z = 0 for 3D space)
  const generateCirclePoints = (radius: number, segments: number): [number, number, number][] => {
    const points: [number, number, number][] = [];
    for (let i = 0; i <= segments; i++) {
      const theta = (i / segments) * Math.PI * 2;
      points.push([Math.cos(theta) * radius, Math.sin(theta) * radius, 0]); // x, y, z
    }
    return points;
  };

  useEffect(() => {
    editorStore.updateGroupRef(circle.id, groupRef.current);
  }, [groupRef.current]);

  // Redraw when the symbol.selected changes
  useEffect(() => {
  }, [circle.selected]);

  useEffect(() => {
    if (isResizing) return;
    setCircleWidth(circle.circleWidth);
    setCircleHeight(circle.circleHeight);
    setLocalPosition([circle.position.x, circle.position.y]);
  }, [isResizing, circle.circleWidth, circle.circleHeight, circle.position.x, circle.position.y]);

  return (
    <group
      position={[0, 0, (circle.zIndex * zIndexEpsilon)]}
    >
      <ShapeProjectionOverlay symbol={circle} />
      <DraggableObject
        position={localPosition}
        onDragStart={(offset) => {
          setIsDragging(true);
          onDragStart(circle, offset)
        }}
        onDragEnd={(endPosition) => {
          onDragEnd(endPosition)
          setIsDragging(false);
        }}
        onDrag={handleDrag}
        selectable={true}
        attachmentId={circle.id}
        attachmentType="noAttachments"
        symbol={circle}
      >
        <SelectableSymbol
          handleSize={floorplannerStore.symbolHandleSize}
          calculateBoundingBox={calculateBoundingBox}
          onResizeStart={() => setIsResizing(true)}
          onResize={(newWidth, newHeight, _boundingBox, _handle, newPosition) => {
            setCircleWidth(newWidth);
            setCircleHeight(newHeight);
            if (newPosition) {
              setLocalPosition(newPosition);
            }
          }}
          onResizeEnd={(_event, finalResize) => {
            setIsResizing(false);
            if (!finalResize) return;
            floorplannerStore.updateSymbolProperty(circle.id, "circleWidth", finalResize.width);
            floorplannerStore.updateSymbolProperty(circle.id, "circleHeight", finalResize.height);
            if (finalResize.position) {
              floorplannerStore.updateSymbolProperty(circle.id, "position", finalResize.position);
            }
          }}
          center={centerPosition}
          rotation={circle.rotation}
          symbol={circle}
          drawHandles={true}
          pivot={pivot}
          onPointerOver={() => setIsHovered(true)}
          onPointerOut={() => setIsHovered(false)}
          isDragging={isDragging}
          lockAspectRatio={isSymbolAspectRatioLocked(circle.lockAspectRatio)}
        >
          <group
            // Remount when opacity / dash style changes — drei Line often ignores live dash updates.
            key={`circle-opacity-${Math.round(fillOpacity * 100)}-${useDashed ? "dashed" : "solid"}-${getOpacityRemountEpoch(circle.id)}`}
            ref={groupRef}
            rotation={[0, 0, circle.rotation || 0]}
            onPointerEnter={() => {
              if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
            }}
            onPointerLeave={() => {
              if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
            }}
          >
            <mesh
              scale={[circleWidth / circleHeight, 1, 1]}
              renderOrder={baseRenderOrder}
            >
              <circleGeometry args={[circleHeight / 2, 64]} />
              <PatternFillMesh
                fillColor={circle.fillColor ?? 0xffffff}
                fillPattern={circle.fillPattern}
                useWorldSpace={false}
                opacity={projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity}
                transparent={projectionActive || fillIsTransparent}
                side={THREE.DoubleSide}
                depthWrite={!projectionActive && !fillIsTransparent}
                depthTest={true}
              />
            </mesh>
            <Line
              key={`circle-outline-${Math.round((projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity) * 100)}-${useDashed ? "dashed" : "solid"}`}
              points={generateCirclePoints(circleHeight / 2, 64)} // Pass generated points for the oval shape
              color={projectionActive ? 0x94a3b8 : canvasAccentColor(circle.selected, !!isHovered, lineColor)}
              lineWidth={projectionActive ? 0.8 : lineWeight}
              scale={[circleWidth / circleHeight, 1, 1]} // Apply scale to the line to match the oval shape
              renderOrder={baseRenderOrder + 0.1}
              opacity={projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity}
              transparent={projectionActive || fillIsTransparent}
              depthWrite={!projectionActive && !fillIsTransparent}
              {...(useDashed ? PLAN_OVERHEAD_DASH : {})}
              raycast={noopRaycast}
            />
          </group>

        </SelectableSymbol>
      </DraggableObject>
    </group>
  );
});

export default CircleSymbol;
